import React, { useState, useEffect, useCallback } from "react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, BarChart, Bar, Legend
} from "recharts";
import { Card, Grid, Typography, Box, Button, Menu, MenuItem, Skeleton } from "@mui/material";
import { BaseTable } from "../BaseTable/BaseTable";
import { GRAPH_TYPE, STATS_ICONS, STATS_LABELS, STATS_TYPE, topPromoCodeColumns, topPromoCodeConColumns } from "./Promo.constant";
import { Icon } from "@iconify/react";
import Page from "../Page";
import { BackButton } from "src/common/components/BackButton";
import { useFetchOnDemand } from "src/hooks/useFetch.hook";
import dayjs from "dayjs";
import { saveMultiSheetToExcel } from "src/utils/convertAnddownloadCsv";

export default function PromoCodeAnalytics() {
  const [anchorElDate, setAnchorElDate] = useState(null);
  const [selectedDate, setSelectedDate] = useState("Last 7 days");

  // --- Hook for stats (top cards)
  const { fetchData: fetchStatsUsage, isLoading: statsUsageLoading, data: statsUsageData } = useFetchOnDemand("promoStats", !1, !1);
  const { fetchData: fetchStatsRevenue, isLoading: statsRevenueLoading, data: statsRevenueData } = useFetchOnDemand("promoStats", !1, !1);
  const { fetchData: fetchStatsAvgDiscount, isLoading: statsAvgDiscountLoading, data: statsAvgDiscountData } = useFetchOnDemand("promoStats", !1, !1);
  const { fetchData: fetchStatsConversionRate, isLoading: statsConversionRateLoading, data: statsConversionRateData } = useFetchOnDemand("promoStats", !1, !1);

  // --- Hook for graph (charts + table)
  const { fetchData: fetchUsageGraph, isLoading: graphUsageLoading, data: graphUsageData } = useFetchOnDemand("promoGraph", !1, !1);
  const { fetchData: fetchRevenueGraph, isLoading: graphRevenueLoading, data: graphRevenueData } = useFetchOnDemand("promoGraph", !1, !1);
  const { fetchData: fetchTopPerformingGraph, isLoading: graphTopPerformingLoading, data: graphTopPerformingData } = useFetchOnDemand("promoGraph", !1, !1);
  const { fetchData: fetchConversionRateGraph, isLoading: graphConversionRateLoading, data: graphConversionRateData } = useFetchOnDemand("promoGraph", !1, !1);
  const { fetchData: fetchStatusGraph, isLoading: graphStatusLoading, data: graphStatusData } = useFetchOnDemand("promoGraph", !1, !1);

  const handleSelectedDate = (selectedDate) => {
    // Utility to format yyyy-mm-dd
    const formatDate = (date) => date.toISOString().split("T")[0];

    const today = new Date();
    let start, end, baseStart, baseEnd;

    switch (selectedDate) {
      case "Last 7 days":
        end = today;
        start = new Date();
        start.setDate(end.getDate() - 6); // last 7 days inclusive
        baseEnd = new Date(start);
        baseEnd.setDate(baseEnd.getDate() - 1);
        baseStart = new Date(baseEnd);
        baseStart.setDate(baseEnd.getDate() - 6);
        break;

      case "Last 30 days":
        end = today;
        start = new Date();
        start.setDate(end.getDate() - 29);
        baseEnd = new Date(start);
        baseEnd.setDate(baseEnd.getDate() - 1);
        baseStart = new Date(baseEnd);
        baseStart.setDate(baseEnd.getDate() - 29);
        break;

      case "Last 90 days":
        end = today;
        start = new Date();
        start.setDate(end.getDate() - 89);
        baseEnd = new Date(start);
        baseEnd.setDate(baseEnd.getDate() - 1);
        baseStart = new Date(baseEnd);
        baseStart.setDate(baseEnd.getDate() - 89);
        break;

      case "Last year":
        end = today;
        start = new Date();
        start.setFullYear(end.getFullYear() - 1);
        baseEnd = new Date(start);
        baseEnd.setDate(baseEnd.getDate() - 1);
        baseStart = new Date(baseEnd);
        baseStart.setFullYear(baseEnd.getFullYear() - 1);
        break;

      default:
        end = today;
        start = new Date();
        start.setDate(end.getDate() - 6);
        baseEnd = new Date(start);
        baseEnd.setDate(baseEnd.getDate() - 1);
        baseStart = new Date(baseEnd);
        baseStart.setDate(baseEnd.getDate() - 6);
    }

    return {
      start: formatDate(start),
      end: formatDate(end),
      baseStart: formatDate(baseStart),
      baseEnd: formatDate(baseEnd),
    };
  }

  useEffect(() => {
    const filters = handleSelectedDate(selectedDate);
    fetchStatsUsage({ ...filters, type: STATS_TYPE.TOTAL_USAGE });
    fetchStatsRevenue({ ...filters, type: STATS_TYPE.TOTAL_REVENUE });
    fetchStatsAvgDiscount({ ...filters, type: STATS_TYPE.AVG_DISCOUNT_PERCENT });
    fetchStatsConversionRate({ ...filters, type: STATS_TYPE.CONVERSION_RATE_PERCENT });

    const graphFilters = { ...filters };
    delete graphFilters["baseStart"];
    delete graphFilters["baseEnd"];
    fetchUsageGraph({ ...graphFilters, type: GRAPH_TYPE.USAGE })
    fetchRevenueGraph({ ...graphFilters, type: GRAPH_TYPE.REVENUE })
    fetchTopPerformingGraph({ ...graphFilters, type: GRAPH_TYPE.TOP_PERFORMING })
    fetchConversionRateGraph({ ...graphFilters, type: GRAPH_TYPE.CONVERSION_RATE })
    fetchStatusGraph({ ...graphFilters, type: GRAPH_TYPE.STATUS })

  }, [selectedDate]);

  const formattedGraphRevenueData = (graphRevenueData ?? []).map((item) => ({
    day: dayjs(item.startDate).format("DD MMM YYYY"),  // e.g. "02 Sep 2023"
    revenue: item.revenue,                   // map transactions → revenue
  }));

  const openDateMenu = Boolean(anchorElDate);

  const handleExportReport = () => {
    const sheets = [
      {
        sheetName: "Stats",
        data: [
          {
            Metric: "Total Usage",
            Value: statsUsageData?.count ?? "",
            "Change %": statsUsageData?.percentChange ?? "",
          },
          {
            Metric: "Total Revenue",
            Value: statsRevenueData?.count ?? "",
            "Change %": statsRevenueData?.percentChange ?? "",
          },
          {
            Metric: "Avg Discount",
            Value: statsAvgDiscountData?.count ?? "",
            "Change %": statsAvgDiscountData?.percentChange ?? "",
          },
          {
            Metric: "Conversion Rate",
            Value: statsConversionRateData?.count ?? "",
            "Change %": statsConversionRateData?.percentChange ?? "",
          },
        ],
      },
      {
        sheetName: "Usage Trend",
        data: (graphUsageData ?? []).map((row) => ({
          Date: row?.startDate ? dayjs(row.startDate).format("DD-MM-YYYY") : "",
          Usage: row?.transactions ?? "",
        })),
      },
      {
        sheetName: "Revenue Trend",
        data: (graphRevenueData ?? []).map((row) => ({
          Date: row?.startDate ? dayjs(row.startDate).format("DD-MM-YYYY") : "",
          Revenue: row?.revenue ?? "",
        })),
      },
      {
        sheetName: "Top Promo Codes",
        data: (graphTopPerformingData ?? []).map((row) => ({
          Code: row?.code ?? "",
          Usage: row?.usageCount ?? "",
          Revenue: row?.totalRevenue ?? "",
        })),
      },
    ];

    saveMultiSheetToExcel(sheets, "Promo_Report");
  };

  // Format dynamic status graph data
const formattedStatusData = (graphStatusData && graphStatusData[0])
  ? [
      { name: "Active", value: Number(graphStatusData[0]?.active_count ?? 0), color: "#00C49F" },
      { name: "Inactive", value: Number(graphStatusData[0]?.inactive_count ?? 0), color: "#FFBB28" },
      { name: "Expired", value: Number(graphStatusData[0]?.expired_count ?? 0), color: "#FF4D4F" },
    ]
  : [];

  return (
    <Page
      title="Promo Code Analytics | Smart-CMS"
    >
      <BackButton
        text="Back to Promo Codes"
        path="/manage-promo-codes"
      />
      <Card sx={{ p: 2 }}>
        {/* Filter */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
          {/* Left Side: Filters */}
          <Box display="flex" gap={1}>
            {/* Date Filter */}
            <Button
              variant="outlined"
              onClick={(e) => setAnchorElDate(e.currentTarget)}
              endIcon={<Icon icon="mdi:chevron-down" />}
            >
              {selectedDate}
            </Button>
            <Menu
              anchorEl={anchorElDate}
              open={openDateMenu}
              onClose={() => setAnchorElDate(null)}
            >
              {["Last 7 days", "Last 30 days", "Last 90 days", "Last year"].map((option) => (
                <MenuItem
                  key={option}
                  selected={option === selectedDate}
                  onClick={() => {
                    setSelectedDate(option);
                    setAnchorElDate(null);
                  }}
                >
                  {option}
                </MenuItem>
              ))}
            </Menu>
          </Box>


          {/* Right Side: Export */}
          <Button
            variant="outlined"
            startIcon={<Icon icon="mdi:download" />}
            onClick={handleExportReport}
          >
            Export Report
          </Button>
        </Box>
        {/* Top Stats */}
        <Grid container spacing={2}>
          <Grid item xs={3}>
            <StatCard
              title={STATS_LABELS.TOTAL_USAGE}
              value={statsUsageData && Number(statsUsageData?.count).toFixed(0)}
              change={statsUsageData && statsUsageData?.percentChange}
              positive={statsUsageData?.percentChange >= 0 ? true : false}
              icon={STATS_ICONS.TOTAL_USAGE}
              selectedDate={selectedDate}
              isLoading={statsUsageLoading}
            />
          </Grid>
          <Grid item xs={3}>
            <StatCard
              title={STATS_LABELS.TOTAL_REVENUE}
              value={statsRevenueData && statsRevenueData?.count}
              change={statsRevenueData && statsRevenueData?.percentChange}
              positive={statsRevenueData?.percentChange >= 0 ? true : false}
              icon={STATS_ICONS.TOTAL_REVENUE}
              selectedDate={selectedDate}
              isLoading={statsRevenueLoading}
            />
          </Grid>
          <Grid item xs={3}>
            <StatCard
              title={STATS_LABELS.AVG_DISCOUNT_PERCENT}
              value={statsAvgDiscountData && statsAvgDiscountData?.count}
              change={statsAvgDiscountData && statsAvgDiscountData?.percentChange}
              positive={statsAvgDiscountData?.percentChange >= 0 ? true : false}
              icon={STATS_ICONS.AVG_DISCOUNT_PERCENT}
              selectedDate={selectedDate}
              isLoading={statsAvgDiscountLoading}
            />
          </Grid>
          <Grid item xs={3}>
            <StatCard
              title={STATS_LABELS.CONVERSION_RATE_PERCENT}
              value={statsConversionRateData && statsConversionRateData?.count}
              change={statsConversionRateData && statsConversionRateData?.percentChange}
              positive={statsConversionRateData?.percentChange >= 0 ? true : false}
              icon={STATS_ICONS.CONVERSION_RATE_PERCENT}
              selectedDate={selectedDate}
              isLoading={statsConversionRateLoading}
            />
          </Grid>
        </Grid>

        <Card sx={{ p: 2, mt: 2 }}>
          <ChartCard title="Usage Trend">
            {graphUsageLoading ? (
              <Skeleton variant="rectangular" height={250} animation="wave" />
            ) : (
              <ResponsiveContainer width="100%" height={250}>
                <LineChart
                  data={(graphUsageData ?? []).map((item) => ({
                    day: dayjs(item.startDate).format("DD MMM YYYY"), // e.g. "02 Sep 2023"
                    usage: item.transactions,
                  }))}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="day" />
                  <YAxis />
                  <Tooltip />
                  <Line
                    type="monotone"
                    dataKey="usage"
                    stroke="#00C49F"
                    strokeWidth={2}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </Card>

        <Card sx={{ mt: 2, p: 2 }}>
          <ChartCard title="Revenue Generated by Promo Codes">
            {graphRevenueLoading ? (
              <Skeleton variant="rectangular" height={300} animation="wave" />
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={formattedGraphRevenueData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="day" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="revenue" fill="#00C49F" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </Card>

        {/* Top Promo Codes Table */}
        <Card sx={{ mt: 2, p: 2 }}>
          <ChartCard title="Top Performing Promo Codes">
            <BaseTable
              rows={graphTopPerformingData ?? []}
              columns={topPromoCodeColumns}
              getRowId={(row) => row?.code}
              pageSize={10}
              isLoading={graphTopPerformingLoading}
            />
          </ChartCard>
        </Card>

        {/* Charts */}
        <Card sx={{ mt: 2, p: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h6" component="h3" sx={{ fontWeight: 600 }}>
                The data displayed below is not affected by the date filter applied above.
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Card sx={{ p: 2 }}>
                <ChartCard title="Promo Code Status">
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie data={formattedStatusData} dataKey="value" nameKey="name" outerRadius={80}>
                        {formattedStatusData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Legend />
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </ChartCard>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Card sx={{ p: 2 }}>
                <ChartCard title="Top Performing Promo Codes Conversion">
                  <BaseTable
                    rows={graphConversionRateData ?? []}
                    columns={topPromoCodeConColumns}
                    getRowId={(row) => row?.code}
                    pageSize={4}
                    isLoading={graphConversionRateLoading}
                  />
                </ChartCard>
              </Card>
            </Grid>
          </Grid>
        </Card>

      </Card>
    </Page>
  );
}

function StatCard({ title, value, change, positive, icon, selectedDate, isLoading }) {
  const ArrowIcon = positive ? "mdi:arrow-up-bold" : "mdi:arrow-down-bold";
  return (
    <Card sx={{ p: 2 }}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={10}>
          <Typography variant="body2" color="text.secondary">{title}</Typography>

          <Typography variant="h4" component="h2" fontWeight="bold">
            {isLoading ? <Skeleton width={80} /> : value ?? "-"}
          </Typography>

          <Typography variant="body2" color={positive ? "success.main" : "error.main"}>
            {isLoading ? (
              <Skeleton width={120} />
            ) : (
              <>
                <Icon icon={ArrowIcon} width={16} height={16} style={{ verticalAlign: "middle" }} />{" "}
                {Math.abs(change ?? 0)}% {positive ? "Increase" : "Decrease"} Compared To The {selectedDate}
              </>
            )}
          </Typography>
        </Grid>

        <Grid item xs={2} display="flex" justifyContent="flex-end">
          {isLoading ? (
            <Skeleton variant="circular" width={24} height={24} />
          ) : (
            <Icon icon={icon} width={24} height={24} />
          )}
        </Grid>
      </Grid>
    </Card>
  );
}

function ChartCard({ title, children }) {
  return (
    <div className="p-4 bg-white shadow rounded">
      <Typography variant="h6" component="h3" sx={{ mb: 2, fontWeight: 600 }}>
        {title}
      </Typography>
      {children}
    </div>
  );
}
